import {
    FC,
    PropsWithChildren,
    ReactElement,
    useState,
} from 'react'
import {
    arrow,
    autoUpdate,
    flip,
    FloatingArrow,
    FloatingPortal,
    offset,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
} from '@floating-ui/react'

import './ElementWithTooltip.scss'

interface ElementWithTooltipProps extends PropsWithChildren {
    tooltipIsActive?: boolean
    tooltipText?: string
    className?: string
}

const arrowSize = 6

export const ElementWithTooltip: FC<ElementWithTooltipProps> = ({
    tooltipIsActive = false,
    tooltipText,
    children,
    className = '',
}): ReactElement => {
    const [isOpen, setIsOpen] = useState(false)
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
    const [floatingElement, setFloatingElement] = useState<HTMLElement | null>(null)
    const [arrowElement, setArrowElement] = useState<SVGSVGElement | null>(null)

    const { floatingStyles, context } = useFloating({
        elements: { reference: referenceElement, floating: floatingElement },
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'top',
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(arrowSize),
            flip(),
            shift({ padding: 8 }),
            arrow({ element: arrowElement }),
        ],
    })

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useHover(context, { mouseOnly: false }),
        useFocus(context),
        useDismiss(context),
        useRole(context, { role: 'tooltip' }),
    ])

    if (! tooltipIsActive) {
        return <>{children}</>
    }

    return (
        <>
            <span
                ref={setReferenceElement}
                className={`element-with-tooltip ${className}`}
                {...getReferenceProps()}
            >
                {children}
            </span>

            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={setFloatingElement}
                        style={floatingStyles}
                        className="element-with-tooltip__tooltip"
                        {...getFloatingProps()}
                    >
                        {tooltipText}
                        <FloatingArrow
                            ref={setArrowElement}
                            context={context}
                            width={arrowSize * 2}
                            height={arrowSize}
                            className="element-with-tooltip__arrow"
                        />
                    </div>
                </FloatingPortal>
            )}
        </>
    )
}
